import React, { useRef, useState } from 'react';
import { VendorContractsInitResult } from '../../../Admin/BuyerMarketplace/OpportunityDetails/LoiSubmissionPackage/useVendorContractsInit';
import { VendorContractsTable } from '../../../Admin/BuyerMarketplace/OpportunityDetails/LoiSubmissionPackage/LoiAndTermsForm/VendorContractsTable';
import { VendorContractPayoff } from '../../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import { saveVendorContractPayoff } from '../../../../slices/marketplace/loi-comparison/loi-comparison-api';
import { useStateSelector } from '../../../../store/selectors';
import createNotification from '../../../../utils/createNotification';
import './LoiComparisonVendorContractsTable.scss';

export interface LoiComparisonVendorContractsTableProps {
    vendorContracts: VendorContractsInitResult;
    selectedRowIds: number[];
    loiFormId: string | null;
    payoffs: VendorContractPayoff[];
    onPayoffSaved?: (saved: VendorContractPayoff) => void;
}

type PayoffRowState = {
    id: string;
    estimatedPayOffAmount: number | null;
    calculationNotes: string | null;
};

const buildPayoffByRow = (payoffs: VendorContractPayoff[]): Record<number, PayoffRowState> => {
    const result: Record<number, PayoffRowState> = {};
    for (const payoff of payoffs) {
        result[payoff.tableRowId] = {
            id: payoff.id,
            estimatedPayOffAmount: payoff.estimatedPayOffAmount,
            calculationNotes: payoff.calculationNotes,
        };
    }
    return result;
};

export const LoiComparisonVendorContractsTable = ({
    vendorContracts,
    selectedRowIds,
    loiFormId,
    payoffs,
    onPayoffSaved,
}: LoiComparisonVendorContractsTableProps) => {
    const axios = useStateSelector((state) => state.core.axios);
    const [payoffByRow, setPayoffByRow] = useState<Record<number, PayoffRowState>>(() => buildPayoffByRow(payoffs));
    const payoffByRowRef = useRef(payoffByRow);
    payoffByRowRef.current = payoffByRow;

    if (!vendorContracts.hasVendorContracts || vendorContracts.vendorContractColumns.length === 0) {
        return null;
    }

    const handlePayoffCommit = (
        tableRowId: number,
        field: 'estimatedPayOffAmount' | 'calculationNotes',
        value: number | string | null
    ) => {
        if (!axios || !loiFormId) {
            return;
        }
        const existing = payoffByRowRef.current[tableRowId];
        const estimatedPayOffAmount = field === 'estimatedPayOffAmount'
            ? (value as number | null)
            : existing?.estimatedPayOffAmount ?? null;
        const calculationNotes = field === 'calculationNotes'
            ? (value as string | null)
            : existing?.calculationNotes ?? null;
        saveVendorContractPayoff(axios, loiFormId, {
            id: existing?.id,
            tableRowId,
            estimatedPayOffAmount,
            calculationNotes,
        })
            .then((saved) => {
                const next = {
                    id: saved.id,
                    estimatedPayOffAmount: saved.estimatedPayOffAmount,
                    calculationNotes: saved.calculationNotes,
                };
                payoffByRowRef.current = { ...payoffByRowRef.current, [tableRowId]: next };
                setPayoffByRow((prev) => ({ ...prev, [tableRowId]: next }));
                onPayoffSaved?.(saved);
            })
            .catch(() => createNotification('Failed to save liability payoff.', 'error'));
    };

    const totalPayOffAmount = vendorContracts.vendorContractTableRows
        .filter((r) => !selectedRowIds.includes(r.tableRowId))
        .reduce((sum, r) => sum + (payoffByRow[r.tableRowId]?.estimatedPayOffAmount ?? 0), 0);

    return (
        <div className="loi-vendor-contracts">
            <h4 className="loi-sec-title">Liability Payoffs</h4>
            <VendorContractsTable
                columns={vendorContracts.vendorContractColumns}
                tableRows={vendorContracts.vendorContractTableRows}
                rowDisplayData={vendorContracts.vendorContractRowDisplayData}
                selectedRowIds={selectedRowIds}
                isReadonly={true}
                projectId={vendorContracts.vendorContractProjectId}
                tableId={vendorContracts.vendorContractTableId}
                fileColumnName={vendorContracts.vendorContractFileColumnName}
                fileColumnId={vendorContracts.vendorContractFileColumnId}
                isAdmin={false}
                showAssumptionLabel={true}
                showPayoffColumns={true}
                payoffByRow={payoffByRow}
                isPayoffEditable={true}
                onPayoffCommit={handlePayoffCommit}
                payoffTotal={totalPayOffAmount}
            />
        </div>
    );
};
